import {useRef, useCallback} from "react"
import {
    calculateImageDimensions,
    calculateCenterPosition,
} from "../utils/calculationsUtils"
import {createCanvasFromVideo} from "../utils/createCanvasFromVideoUtils"
import {CONSTANTS} from "../constants/constants"
import type {CropperHookReturn} from "../types/types"

interface UseImageLoadProps {
    materialIndex: number
    cropper: CropperHookReturn
}

export const useImageLoad = ({materialIndex, cropper}: UseImageLoadProps) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)

    const handleImageLoad = useCallback(
        (img: HTMLImageElement, imgUrl: string, file: File) => {
            if (!cropper) return

            const dimensions = calculateImageDimensions(
                img,
                cropper.cropState.dimensions
            )
            const position = calculateCenterPosition(dimensions)

            cropper.setImageState({
                status: "loaded",
                file: file, // ✅ File объект
                imgUrl: imgUrl,
                dimensions,
                position,
                zoom: 100,
                videoPlaying: false,
            })

            cropper.setCurrentImageUrl(imgUrl)

            cropper.setRenderData({
                position,
                dimensions,
            })
        },
        [cropper]
    )

    const imageUpload = useCallback(
        (file: File, reader: FileReader) => {
            if (!cropper) return

            cropper.setMode("crop")

            reader.onload = (e) => {
                if (e.target?.result) {
                    const dataUrl = e.target.result as string

                    if (file.type.startsWith("video/")) {
                        const video = document.createElement("video")
                        document.body.appendChild(video)
                        video.id = `video${materialIndex}`
                        video.className = "cropVideo"
                        video.loop = true
                        video.style.display = "none"
                        video.src = dataUrl

                        video.onloadedmetadata = () => {
                            cropper.setImageState({
                                videoDuration: video.duration,
                            })

                            video.onseeked = () => {
                                const {stillImage} =
                                    createCanvasFromVideo(video)

                                stillImage.onload = () => {
                                    videoRef.current = video
                                    cropper.setVideoElement(video)
                                    handleImageLoad(
                                        stillImage,
                                        stillImage.src,
                                        file
                                    ) // ✅
                                    cropper.setImageState({
                                        videoUrl: video.src,
                                        file: file, // ✅ Обновляем file для видео
                                    })
                                }
                            }
                            video.currentTime = 0
                        }

                        video.onerror = () => {
                            console.error("Ошибка загрузки видео")
                        }
                    } else if (file.type.startsWith("image/")) {
                        const img = new Image()
                        img.onload = () => handleImageLoad(img, dataUrl, file) // ✅
                        img.src = dataUrl
                    }
                }
            }
            reader.readAsDataURL(file)
        },
        [cropper, materialIndex, handleImageLoad]
    )

    const handleImageUpload = useCallback(
        (file: File) => {
            if (!cropper) return

            if (videoRef.current) {
                videoRef.current.pause()
                videoRef.current.src = ""
                videoRef.current.load()
                videoRef.current.remove()
                videoRef.current = null
            }

            cropper.setVideoElement(null)
            cropper.setCurrentImageUrl(null)

            // ✅ Сброс в initial состояние (без file)
            cropper.setImageState({
                status: "initial",
                dimensions: {width: 0, height: 0},
                position: {x: 0, y: 0},
                zoom: 100,
                videoPlaying: false,
            })

            if (
                (file.type.startsWith("video/") &&
                    file.size > CONSTANTS.MAX_VIDEO_FILE_SIZE) ||
                (file.type.startsWith("image/") &&
                    file.size > CONSTANTS.MAX_IMAGE_FILE_SIZE)
            ) {
                console.error("File size too large")
                return
            }

            const reader = new FileReader()
            imageUpload(file, reader)
        },
        [cropper, imageUpload]
    )

    return {
        videoRef,
        handleImageUpload,
    }
}
